import { client } from "@/client";
import { toast } from "react-toastify";
import { AddConfigAdministratorMutation, AddConfigAdministratorMutationVariables, AddConfigAdministratorDocument, UserOnEntity } from "@/graphql/__generated__/graphql";
import { useTranslation } from "react-i18next";
import { CustomToastComponent } from "@/components/shared";
import { useMutation } from "@apollo/client/react";

/**
 * Custom hook for adding a config administrator.
 * @param {Object} options - Optional configuration options
 * @param {boolean} [options.disableToast=false] - Whether to disable the success toast
 * @param {boolean} [options.disableRefetch=false] - Whether to disable refetching queries
 * @returns {Object} - Contains the addConfigAdministratorMutation function, loading state, and error state.
 */
export default function useAddConfigAdministratorMutationHook(): { addConfigAdministratorMutation: any, loading: any, error: any} {

  // Hooks
  const { t } = useTranslation(["toasts"]);

  const [addConfigAdministratorMutation, { loading, error }] = useMutation<AddConfigAdministratorMutation, AddConfigAdministratorMutationVariables>(
    AddConfigAdministratorDocument, {
      onCompleted: (data) => {

        toast(CustomToastComponent, {
          toastId: "config-administrator-added-success",
          data: {
            title: t("toasts.configadministratoraddedsuccessfully"),
            type: "success"
          }
        });

        updatePaginatedCache(data.addConfigAdministrator);
      }
    });

  /**
   * Updates the paginated config administrators cache by adding the new config administrator.
   * @param {UserOnEntity} newConfigAdministrator - The new config administrators to be added.
   */
  const updatePaginatedCache = (newConfigAdministrator: UserOnEntity) => {
    // Use cache.modify to update the paginatedConfigAdministrators query on ROOT_QUERY
    client.cache.modify({
      fields: {
        paginatedConfigAdministrators(existingData, { readField: _readField }) {
          if (!existingData?.edges) {
            return existingData;
          }

          // Create new edges for the new administrators
          const newEdges = {
            __typename: "UserEdge",
            node: newConfigAdministrator,
            cursor: String(newConfigAdministrator.id)
          };

          // Add new edges to the beginning of the existing edges (most recent first)
          return {
            ...existingData,
            edges: [newEdges, ...existingData.edges]
          };
        }
      }
    });
  };

  return {
    addConfigAdministratorMutation,
    loading,
    error
  };
}
